/**
 * Reading an organization's profile AS SHOWN: office profiles 2B-4 (#893).
 *
 * `public.org_effective_profiles` (migration 20261014040000, 2A) holds one row
 * per organization with its profile resolved: a managed office's inherited
 * groups come from its parent, a group it does not offer is empty and named
 * in `confirmed_none`, an unknown group is NULL. For every organization that is
 * not a managed office the row equals the organization's own data. The public
 * RPCs (2B-2), the fit score (2B-1), the CMO view and the Edge Functions
 * (2B-3) already read it. This module is the one place the browser reads the
 * profile from it; `src/lib/fitScoreCache.ts` reads its two version columns
 * (`effective_version`, `review_revision`) for the fit-cache contract (2B-5).
 *
 * It owns two things, so the readers cannot drift from each other:
 *
 * 1. THE COLUMN LIST. Every reader embeds the effective row through
 *    EFFECTIVE_PROFILE_EMBED (the whole profile) or EFFECTIVE_LOGO_EMBED (logo
 *    only) inside its existing `organizations` select; the one-to-one foreign
 *    key lets PostgREST nest it (inventory D5).
 * 2. THE OVERLAY. resolveEffectiveOrganization() returns the organizations row
 *    with every effective column written over it, unconditionally: a reader
 *    that also selected the organization's own `logo_url` or `profile_data`
 *    still sees the effective value, never its own. A missing embed resolves
 *    to null rather than falling back to the own row, because for a managed
 *    office the own row shows content the office hides and misses content it
 *    inherits. 2A's trigger writes an effective row for every organization,
 *    and its SELECT policy follows the organization's own visibility, so a
 *    missing embed means that invariant broke; the caller treats the
 *    organization as unreadable.
 *
 * Parent-only fields (spec 8.1, inventory D11) are never shown on a managed
 * office: the overlay nulls `founded_year` and removes `foundedYear`,
 * `yearsInBusiness` and `franchise` from the profile document when
 * `is_managed` is true, so every founded-year, "Est.", years-in-business and
 * franchise-label reader downstream suppresses them without knowing why. A
 * standalone organization's values pass through untouched.
 *
 * Columns in no group (`name`, `slug`, `description`, `website`, `type`,
 * `service_types`, `service_tags`, `office_locations`, `city`, `state`,
 * `is_published`, `is_seed`, `is_hiring`, ...) stay on `organizations`: an
 * office's effective value is its own value (inventory D10).
 *
 * The editors do NOT read through this module. `useOrgStoryDraft`, the
 * Console edit page and the setup wizard's steps read the organization's own
 * row, because that is what they write; showing them inherited values would
 * copy the parent's content into the office on the next save. The admin and
 * Console STRENGTH displays do read the resolved row (D8, approved by Jesse on
 * 2026-10-14), so the percentage an admin sees agrees with the reminder emails
 * and the CMO view.
 *
 * `src/lib/orgEffectiveProfileCallsites.test.ts` fails the suite when a browser
 * read of `organizations` selects a profile column without the embed, unless
 * the file is listed there with its reason.
 */

/** The effective columns every full reader overlays, in select order. */
export const EFFECTIVE_PROFILE_COLUMNS = [
  "profile_data",
  "logo_url",
  "cover_url",
  "compensation_models",
  "no_recurring_fees",
  "provides_leads",
  "lead_sources",
  "lead_cost",
  "lead_volume",
  "training_frequency",
  "selected_benefits",
  "partnership_opportunities",
  "partnership_types",
  "partnership_eligibility",
  "confirmed_none",
  "is_managed",
  "effective_version",
] as const;

export type EffectiveProfileColumn = (typeof EFFECTIVE_PROFILE_COLUMNS)[number];

/**
 * The organizations columns the overlay replaces: every effective column that
 * also exists on `organizations`. A browser read of any of these from the
 * organizations row is an own-row read (`orgEffectiveProfileCallsites.test.ts`).
 */
export const OVERLAID_ORGANIZATION_COLUMNS: readonly string[] = EFFECTIVE_PROFILE_COLUMNS.filter(
  (c) => c !== "confirmed_none" && c !== "is_managed" && c !== "effective_version",
);

/** The PostgREST embed for the whole effective profile, to nest in an `organizations` select. */
export const EFFECTIVE_PROFILE_EMBED = `org_effective_profiles(${EFFECTIVE_PROFILE_COLUMNS.join(", ")})`;

/** The embed for readers that show the logo and nothing else of the profile. */
export const EFFECTIVE_LOGO_EMBED = "org_effective_profiles(logo_url)";

/**
 * The profile document keys that belong to the parent and are never shown on
 * a managed office (spec 8.1): founded year, years in business, the franchise
 * label. `founded_year` is the column form of the first.
 */
export const PARENT_ONLY_PROFILE_KEYS = ["foundedYear", "yearsInBusiness", "franchise"] as const;

/** The fields the overlay writes onto the organizations row. */
export interface EffectiveProfileFields {
  profile_data: Record<string, unknown> | null;
  logo_url: string | null;
  cover_url: string | null;
  compensation_models: string[] | null;
  no_recurring_fees: boolean | null;
  provides_leads: boolean | null;
  lead_sources: string[] | null;
  lead_cost: string | null;
  lead_volume: string | null;
  training_frequency: string[] | null;
  selected_benefits: string[] | null;
  partnership_opportunities: boolean | null;
  partnership_types: string[] | null;
  partnership_eligibility: string | null;
  /** The groups the office said it does not offer ("Not offered here", spec 8.3). */
  confirmed_none: string[];
  /** True for a managed office (confirmed link, activated company). */
  is_managed: boolean;
  /** Moves when the resolved content changes; what the fit-score cache is keyed on (2B-5). */
  effective_version: number;
  /** Nulled on a managed office; the own column otherwise (null when the reader did not select it). */
  founded_year: number | string | null;
}

/** A row read with EFFECTIVE_PROFILE_EMBED or EFFECTIVE_LOGO_EMBED nested in it. */
export interface WithEffectiveEmbed {
  org_effective_profiles?: unknown;
}

/** An organizations row with its effective profile written over it. */
export type ResolvedOrganization<T extends WithEffectiveEmbed> =
  Omit<T, "org_effective_profiles" | keyof EffectiveProfileFields> & EffectiveProfileFields;

/**
 * The embedded effective row, or null when it is missing or malformed.
 * PostgREST returns a to-one embed as an object; a one-element array is
 * accepted too, as the Edge Function loader does.
 */
export function readEffectiveEmbed(raw: unknown): Record<string, unknown> | null {
  const eff = Array.isArray(raw) ? (raw.length === 1 ? raw[0] : null) : raw;
  if (!eff || typeof eff !== "object") return null;
  return eff as Record<string, unknown>;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

const stringArrayOrNull = (v: unknown): string[] | null =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : null;
const booleanOrNull = (v: unknown): boolean | null => (typeof v === "boolean" ? v : null);
const textOrNull = (v: unknown): string | null => (typeof v === "string" ? v : null);

/**
 * The organizations row with its effective profile written over it, or null
 * when the effective row did not come back (see the module comment). Pure: the
 * input is not mutated, and the profile document is copied before parent-only
 * keys are removed.
 */
export function resolveEffectiveOrganization<T extends WithEffectiveEmbed>(
  row: T,
): ResolvedOrganization<T> | null {
  const e = readEffectiveEmbed(row.org_effective_profiles);
  if (!e) return null;
  const { org_effective_profiles: _embed, ...own } = row;

  const isManaged = e.is_managed === true;
  const profileData: Record<string, unknown> | null = isPlainObject(e.profile_data) ? { ...e.profile_data } : null;
  if (isManaged && profileData) {
    for (const key of PARENT_ONLY_PROFILE_KEYS) delete profileData[key];
  }

  const fields: EffectiveProfileFields = {
    profile_data: profileData,
    logo_url: textOrNull(e.logo_url),
    cover_url: textOrNull(e.cover_url),
    compensation_models: stringArrayOrNull(e.compensation_models),
    no_recurring_fees: booleanOrNull(e.no_recurring_fees),
    provides_leads: booleanOrNull(e.provides_leads),
    lead_sources: stringArrayOrNull(e.lead_sources),
    lead_cost: textOrNull(e.lead_cost),
    lead_volume: textOrNull(e.lead_volume),
    training_frequency: stringArrayOrNull(e.training_frequency),
    selected_benefits: stringArrayOrNull(e.selected_benefits),
    partnership_opportunities: booleanOrNull(e.partnership_opportunities),
    partnership_types: stringArrayOrNull(e.partnership_types),
    partnership_eligibility: textOrNull(e.partnership_eligibility),
    confirmed_none: stringArrayOrNull(e.confirmed_none) ?? [],
    is_managed: isManaged,
    effective_version: typeof e.effective_version === "number" ? e.effective_version : Number(e.effective_version ?? 0),
    founded_year: isManaged ? null : ((own as { founded_year?: number | string | null }).founded_year ?? null),
  };
  return { ...(own as Omit<T, "org_effective_profiles">), ...fields } as ResolvedOrganization<T>;
}

/**
 * The organizations row with its `logo_url` taken from the effective row (the
 * embed removed), for readers that show the logo and nothing else of the
 * profile. A missing embed gives a null logo, never the own column.
 */
export function withEffectiveLogo<T extends WithEffectiveEmbed>(
  row: T,
): Omit<T, "org_effective_profiles"> & { logo_url: string | null } {
  const { org_effective_profiles: raw, ...own } = row;
  const e = readEffectiveEmbed(raw);
  return { ...(own as Omit<T, "org_effective_profiles">), logo_url: e ? textOrNull(e.logo_url) : null };
}

/**
 * Every row of a list read, resolved; rows whose effective embed is missing are
 * left out (fail closed, as `loadOrgStrengthInputs` does for the emails), and
 * their ids are warned about, ids only, so a broken 2A invariant does not show
 * up only as a missing card (the #663 lesson: a silent drop looks like no data).
 */
export function resolveEffectiveOrganizations<T extends WithEffectiveEmbed>(
  rows: readonly T[] | null | undefined,
): ResolvedOrganization<T>[] {
  const out: ResolvedOrganization<T>[] = [];
  const dropped: string[] = [];
  for (const row of rows ?? []) {
    const resolved = resolveEffectiveOrganization(row);
    if (resolved) out.push(resolved);
    else dropped.push(String((row as { id?: unknown }).id ?? "?"));
  }
  if (dropped.length > 0) {
    console.warn(`orgEffectiveProfile: ${dropped.length} organization(s) have no effective profile row; not shown`, dropped);
  }
  return out;
}
